import { useState } from 'react';
import { Form, InputGroup } from 'react-bootstrap';
import { normalizeWebAddress } from '../utils/webAddress';

const SCHEME = /^[a-z][a-z0-9+.-]*:\/\//i;

/**
 * A web address box that takes the address however it is typed (example.com, www.example.com, https://...) and,
 * on leaving the box, shows it tidied (https://example.com) - or says plainly what is wrong with it. A plain text
 * box, not type="url": the browser's own check refuses "example.com" without saying why.
 *
 * With `https`, a fixed "https://" sits in front of the box, which then holds only the rest (www.example.com); a
 * scheme typed or pasted into the box is dropped, so "https://https://" can't happen. value and onChange still
 * carry the whole address. For a website; a pasted image link or a development address (http://localhost) is
 * better without it.
 */
function WebAddressInput({ value, onChange, placeholder = 'example.com', https = false, id, ...rest }) {
  const [error, setError] = useState(null);

  const shown = https ? (value || '').replace(/^https?:\/\//i, '') : (value || '');

  const change = (text) => {
    setError(null);
    if (!https) {
      onChange(text);
      return;
    }
    const address = text.trimStart().replace(SCHEME, '').replace(/^\/+/, '');
    onChange(address ? `https://${address}` : '');
  };

  const tidy = () => {
    const result = normalizeWebAddress(value);
    setError(result.error);
    if (!result.error && result.value !== (value || '')) onChange(result.value);
  };

  const box = (
    <Form.Control
      id={id}
      type="text"
      inputMode="url"
      autoComplete="url"
      autoCapitalize="none"
      spellCheck={false}
      value={shown}
      placeholder={placeholder}
      isInvalid={Boolean(error)}
      onChange={(e) => change(e.target.value)}
      onBlur={tidy}
      {...rest}
    />
  );
  const feedback = error && <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>;

  if (!https) {
    return (
      <>
        {box}
        {feedback}
      </>
    );
  }
  return (
    <InputGroup hasValidation>
      <InputGroup.Text className="text-muted" aria-hidden="true">https://</InputGroup.Text>
      {box}
      {feedback}
    </InputGroup>
  );
}

/**
 * For a form's save: the address tidied, or a message to show (named by field) when it isn't a web address.
 * @returns {{ value: string, error: string|null }}
 */
export const checkWebAddress = (label, value) => {
  const result = normalizeWebAddress(value);
  return result.error ? { value: result.value, error: `${label}: ${result.error}` } : result;
};

export default WebAddressInput;
